import React, { useState, useEffect } from "react";
import {
	useTable,
	useResizeColumns,
	useFlexLayout,
	useSortBy,
	useFilters,
	usePagination,
} from "react-table";
import matchSorter from "match-sorter";
import { Pagination } from "react-bootstrap";
import { IoIosArrowRoundDown, IoIosArrowRoundUp } from "react-icons/io";

function fuzzyTextFilterFn(rows, id, filterValue) {
	return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

function FilterTable({ columns, data, defaultPageSize }) {
	const filterTypes = React.useMemo(
		() => ({
			// Add a new fuzzyTextFilterFn filter type.
			fuzzyText: fuzzyTextFilterFn,
			// Or, override the default text filter to use
			// "startWith"
			text: (rows, id, filterValue) => {
				return rows.filter((row) => {
					const rowValue = row.values[id];
					return rowValue !== undefined
						? String(rowValue)
								.toLowerCase()
								.startsWith(String(filterValue).toLowerCase())
						: true;
				});
			},
		}),
		[]
	);

	const defaultColumn = React.useMemo(
		() => ({
			// When using the useFlexLayout:
			minWidth: 70, // minWidth is only used as a limit for resizing
			width: 150, // width is used for both the flex-basis and flex-grow
			maxWidth: 450, // maxWidth is only used as a limit for resizing

			Filter: DefaultColumnFilter, // Let's set up our default Filter UI
		}),
		[]
	);

	const [pageNumber, setPageNumber] = useState(1);
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		page, // Instead of using 'rows', we'll use page,
		// which has only the rows for the active page

		// The rest of these things are super handy, too ;)
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		state: { pageIndex, pageSize },
	} = useTable(
		{
			columns,
			data,
			defaultColumn, // Be sure to pass the defaultColumn option
			filterTypes,
			initialState: {
				pageIndex: pageNumber - 1,
				pageSize: defaultPageSize || 10,
			},
		},
		useResizeColumns,
		useFlexLayout,
		useFilters, // useFilters!
		useSortBy,
		usePagination // usePagination!
	);

	// Pagination
	let items = [];
	let pages = pageOptions.length;
	const [start, setStart] = useState(1);
	const [end, setEnd] = useState(0);

	// Define a default UI for filtering
	function DefaultColumnFilter({
		column: { filterValue, preFilteredRows, setFilter },
	}) {
		return (
			<input
				value={filterValue || ""}
				onChange={(e) => {
					setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
					setPageNumber(1);
				}}
				placeholder={`Search`}
				className="column-filter form-control"
			/>
		);
	}

	for (let number = start; number <= end; number++) {
		items.push(
			<Pagination.Item
				key={number}
				active={number === pageNumber}
				onClick={() => {
					setPageNumber(number);
					return gotoPage(number - 1);
				}}
			>
				{number}
			</Pagination.Item>
		);
	}

	useEffect(() => {
		if (pages < 5) {
			setPageNumber(1);
			setStart(1);
			setEnd(pages);
		}
	}, [pages]);

	useEffect(() => {
		if (pageOptions.length < 5) {
			setStart(1);
			setEnd(pageOptions.length);
		} else if (pageOptions.length >= 5) {
			setStart(1);
			setEnd(5);
		}
	}, [pageSize, pages, data]);

	return (
		<React.Fragment>
			<div className="table-responsive">
				<div {...getTableProps()} className="table divtable">
					<div className="thead">
						{headerGroups.map((headerGroup) => (
							<div
								{...headerGroup.getHeaderGroupProps({
									// style: { paddingRight: '15px' },
								})}
								className="tr"
							>
								{headerGroup.headers.map((column) => (
									<div {...column.getHeaderProps()} className="th">
										<div className="th-wrap">
											<div
												{...column.getHeaderProps(
													column.getSortByToggleProps()
												)}
												title={column.render("Header")}
											>
												<h6
													className="h6 th-header d-block pb-1"
													{...column.getHeaderProps(
														column.getSortByToggleProps()
													)}
													title={column.render("Header")}
												>
													{column.render("Header")}
													{/* <h6 className="h6 th-header d-block" title={column.render('Header')}>{column.render('Header')} */}
													{/* Add a sort direction indicator */}
													<span className="th-sort-icon">
														{column.isSorted ? (
															column.isSortedDesc ? (
																<IoIosArrowRoundDown className="icon th-sort-icon" />
															) : (
																<IoIosArrowRoundUp className="icon th-sort-icon" />
															)
														) : (
															""
														)}
													</span>
												</h6>
											</div>

											{/* Use column.getResizerProps to hook up the events correctly */}
											{column.canResize && (
												<div
													{...column.getResizerProps()}
													className={`resizer ${
														column.isResizing ? "isResizing" : ""
													}`}
												/>
											)}

											{/* Render the columns filter UI */}
											<div className="th-filter-input">
												{column.canFilter ? column.render("Filter") : null}
											</div>
										</div>
									</div>
								))}
							</div>
						))}
					</div>

					<div className="tbody">
						{page.map((row) => {
							prepareRow(row);
							return (
								<div {...row.getRowProps()} className="tr">
									{row.cells.map((cell) => {
										return (
											<div className="td" {...cell.getCellProps()}>
												<div className="cell">{cell.render("Cell")}</div>
											</div>
										);
									})}
								</div>
							);
						})}
					</div>
				</div>
			</div>
			{/* 
Pagination can be built however you'd like. 
This is just a very basic UI implementation:
*/}
			<div className="grid-pagination d-flex">
				<div className="pagination-left d-flex">
					<Pagination>
						<Pagination.First
							onClick={() => {
								setPageNumber(1);
								setStart(1);
								if (pageOptions.length >= 5) {
									setEnd(5);
								} else {
									setEnd(pageOptions.length);
								}
								return gotoPage(0);
							}}
							disabled={!canPreviousPage}
						/>
						<Pagination.Prev
							onClick={() => {
								setPageNumber(pageNumber - 1);
								if (pageNumber >= 5) {
									setStart(pageNumber - 4);
									setEnd(pageNumber);
								} else if (pageNumber <= 5) {
									setStart(1);
									setEnd(5);
								}
								return previousPage();
							}}
							disabled={!canPreviousPage}
						/>
						{/* 1 - 5 pagination */}
						{items}
						<Pagination.Next
							onClick={() => {
								setPageNumber(pageNumber + 1);
								if (pageNumber >= 5) {
									let sum = pageOptions.length - pageNumber;
									if (sum > 4) {
										setEnd(pageNumber + 4);
										setStart(pageNumber);
									} else {
										setEnd(pageOptions.length);
										setStart(pageOptions.length - 4);
									}
								}
								return nextPage();
							}}
							disabled={!canNextPage}
						/>
						<Pagination.Last
							onClick={() => {
								setPageNumber(pageCount);
								setEnd(pageCount);

								if (pageOptions.length >= 5) {
									setStart(pageCount - 4);
								} else {
									setStart(1);
								}
								return gotoPage(pageCount - 1);
							}}
							disabled={!canNextPage}
						/>
					</Pagination>
					<span className="page-of">
						Page{" "}
						<strong>
							{/* {pageIndex + 1} of {pageOptions.length} */}
							{pageNumber} of {pageOptions.length}
						</strong>{" "}
					</span>
				</div>
				<div className="pagination-right d-flex">
					<span>
						Go to page:{" "}
						<input
							type="text"
							// value={pageNumber}
							onChange={(e) => {
								let page = 0;
								const regex = /^[0-9\b]+$/; // for numbers
								const regexzero = /^[^0]\d*$/g;
								if (
									e.target.value === "" ||
									(regex.test(e.target.value) && regexzero.test(e.target.value))
								) {
									page = e.target.value ? Number(e.target.value) - 1 : 0;
								}
								gotoPage(page);
								let currentPageNumber = page + 1;

								if (currentPageNumber > pageOptions.length) {
									if (currentPageNumber < 5 && pageOptions.length < 5) {
										setPageNumber(1);
										setStart(1);
										setEnd(pageOptions.length);
										gotoPage(0);
									} else if (
										currentPageNumber >= 5 &&
										pageOptions.length >= 5
									) {
										setPageNumber(1);
										setStart(1);
										setEnd(5);
										gotoPage(0);
									}
								} else if (currentPageNumber <= 0) {
									setPageNumber(1);
									setStart(1);
									gotoPage(0);
									if (pageOptions.length >= 5) {
										setEnd(5);
									} else {
										setEnd(pageOptions.length);
									}
								} else if (currentPageNumber >= 5) {
									setPageNumber(currentPageNumber);
									let nextThree = currentPageNumber + 3;
									if (nextThree <= pageOptions.length) {
										setStart(currentPageNumber - 1);
										setEnd(currentPageNumber + 3);
									} else if (currentPageNumber === pageOptions.length) {
										setStart(currentPageNumber - 4);
										setEnd(currentPageNumber);
									} else if (currentPageNumber < pageOptions.length) {
										setStart(currentPageNumber - 3);
										setEnd(currentPageNumber + 1);
									} else {
										setStart(currentPageNumber - 3);
										setEnd(pageOptions.length);
									}
								} else if (currentPageNumber < 5) {
									setPageNumber(currentPageNumber);

									if (pageOptions.length >= 5) {
										if (currentPageNumber === 1) {
											setStart(1);
											setEnd(currentPageNumber + 4);
										} else {
											setStart(currentPageNumber - 1);
											setEnd(currentPageNumber + 3);
										}
									} else {
										setStart(1);
										setEnd(pageOptions.length);
									}
									// setEnd(pageOptions.length)
								} else {
									setPageNumber(currentPageNumber);
								}
							}}
							min={pageIndex + 1}
							max={pageOptions.length}
							className="form-control display-inline"
							style={{ width: "80px" }}
						/>
					</span>{" "}
					<select
						value={pageSize}
						onChange={(e) => {
							setPageSize(Number(e.target.value));
							setPageNumber(1);
							gotoPage(0);
							setStart(1);
							if (pageOptions.length >= 5) {
								setEnd(5);
							} else {
								setEnd(pageOptions.length);
							}
						}}
						className="form-control display-inline ml-3"
						style={{ width: "120px" }}
					>
						{[5, 10, 20, 30, 40, 50].map((pageSize) => (
							<option key={pageSize} value={pageSize}>
								Show {pageSize}
							</option>
						))}
					</select>
				</div>
			</div>
		</React.Fragment>
	);
}

export default FilterTable;
